import { Box, IconButton, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from "@mui/material";
import {
    DotLegalDatePicker,
    DotLegalFullScreenSpinner,
    DotLegalHeader,
    DotLegalInformationTooltip,
    DotLegalMultiSelect,
    DotLegalSelect,
} from "@dotlegal/dotlegal-ui-components";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { removeIcon } from "../../icons.tsx";
import React, { useState } from "react";
import { useSelectableAnnualWheelActivityTable } from "./SelectableAnnualWheelActivityTable.hooks.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { SelectableAnnualWheelActivityTableRow } from "./SelectableAnnualWheelActivityTable.types.ts";
import { useRelatedTasksSelectorStyle } from "../../../taskManagement/relatedTasksTab/relatedTasksSelector/RelatedTasksSelector.styles.ts";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender.tsx";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection.tsx";
import { RecurringIntervalType } from "../../../annualWheel/AnnualWheel.types.ts";
import { useRecurringIntervalFormatting, useRecurringIntervalTypeSelectOptions } from "../../../annualWheel/AnnualWheel.hooks.ts";
import RecurrenceSection from "../../../annualWheel/components/RecurrenceSection.tsx";

export interface TemplateSelection {
    templateId: string;
    deadline: Date;
    interval: number | null;
    intervalType: RecurringIntervalType | null;
    groupEntityIds: Array<string>;
}

export interface SelectableAnnualWheelActivityTableProps {
    selectableAnnualWheelActivities: Array<SelectableAnnualWheelActivityTableRow>;
    selectedAnnualWheelActivities: Array<string>;
    onAddAnnualWheelActivity: (id: string) => void;
    onRemoveAnnualWheelActivity: (id: string) => void;
    isLoading: boolean;
    showGroupCompaniesColumn?: boolean;
    groupEntitiesOptions?: Array<DotLegalSelectOption>;
    templateSelections?: Array<TemplateSelection>;
    onTemplateDeadlineChange?: (id: string, deadline: Date) => void;
    onTemplateRecurrenceChange?: (id: string, recurrence: { interval: number | null; intervalType: RecurringIntervalType | null }) => void;
    onTemplateGroupEntitiesChange?: (id: string, groupEntityIds: Array<string>) => void;
}

function SelectableAnnualWheelActivityTable(props: SelectableAnnualWheelActivityTableProps) {
    const styles = useRelatedTasksSelectorStyle();

    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const { selectedAnnualWheelActivities, order, orderBy, onSortClick } = useSelectableAnnualWheelActivityTable(props);
    const formatRecurringInterval = useRecurringIntervalFormatting();
    const recurringIntervalTypeOptions = useRecurringIntervalTypeSelectOptions();
    const [recurrenceEditor, setRecurrenceEditor] = useState<{ rowId: string; anchor: HTMLElement } | null>(null);

    const showPerRowSelectionControls = !!props.templateSelections && !!props.onTemplateDeadlineChange && !!props.onTemplateRecurrenceChange;
    const findSelection = (id: string) => props.templateSelections?.find((s) => s.templateId === id);

    const getSortProps = (name: string) => {
        return {
            active: name === orderBy,
            onClick: () => onSortClick(name),
            direction: name === orderBy ? order : "asc",
        };
    };

    const editingSelection = recurrenceEditor ? findSelection(recurrenceEditor.rowId) : undefined;

    const onRecurrenceIntervalChange = (value: string) => {
        if (!recurrenceEditor || !editingSelection) return;
        const parsed = value === "" ? null : Number(value);
        props.onTemplateRecurrenceChange!(recurrenceEditor.rowId, {
            interval: parsed,
            intervalType: parsed === null ? null : editingSelection.intervalType,
        });
    };

    const onRecurrenceTypeChange = (value: string) => {
        if (!recurrenceEditor || !editingSelection) return;
        props.onTemplateRecurrenceChange!(recurrenceEditor.rowId, {
            interval: editingSelection.interval,
            intervalType: Number(value) as RecurringIntervalType,
        });
    };

    const onRecurrenceSingularChange = (singular: boolean) => {
        if (!recurrenceEditor || !editingSelection) return;
        props.onTemplateRecurrenceChange!(recurrenceEditor.rowId, {
            interval: singular ? null : 1,
            intervalType: singular ? null : (editingSelection.intervalType ?? RecurringIntervalType.Month),
        });
    };

    const getMinDate = () => {
        return new Date(new Date().getFullYear(), 0, 1);
    };

    const getMaxDate = () => {
        return new Date(new Date().getFullYear() + 1, 11, 31);
    };

    return (
        <React.Fragment>
            {props.isLoading ? (
                <DotLegalFullScreenSpinner />
            ) : (
                <>
                    <Box mx="auto" sx={{ width: "50%" }}>
                        <DotLegalSelect
                            options={props.selectableAnnualWheelActivities
                                .filter((x) => !props.selectedAnnualWheelActivities.includes(x.id))
                                .map((x) => {
                                    return { id: x.id, name: x.name, hoverText: x.description ? <MentionInputRender value={x.description} /> : undefined };
                                })}
                            selectedItem={""}
                            onChange={(value) => {
                                props.onAddAnnualWheelActivity(value!);
                            }}
                            placeholder={translateString("addTask")}
                            label={""}
                            noOptionsLabel={translateString("noOptions")}
                            clearOnSelect
                            noMargin
                            alwaysOpenInBottom
                        />
                    </Box>
                    {selectedAnnualWheelActivities.length > 0 && (
                        <Box sx={{ marginTop: 2 }}>
                            <DotLegalSection>
                                <TableContainer sx={(theme) => ({ "& .MuiTableCell-root": { color: theme.palette.primary.dark } })}>
                                    <Table size="small">
                                        <TableHead sx={{ textTransform: "uppercase" }}>
                                            <TableRow sx={{ border: 0 }}>
                                                <TableCell sx={styles.tableCellLeft} align="left">
                                                    <TableSortLabel {...getSortProps("name")}>
                                                        <DotLegalHeader marginTop={0} marginBottom={0} headerStyle="extraSmall">
                                                            {translateString("task")}
                                                        </DotLegalHeader>
                                                    </TableSortLabel>
                                                </TableCell>

                                                <TableCell sx={styles.tableCellLeft} align="left">
                                                    <TableSortLabel {...getSortProps("customId")}>
                                                        <DotLegalHeader marginTop={0} marginBottom={0} headerStyle="extraSmall">
                                                            {translateString("id")}
                                                        </DotLegalHeader>
                                                    </TableSortLabel>
                                                </TableCell>

                                                {showPerRowSelectionControls && (
                                                    <TableCell sx={{ ...styles.tableCellLeft, width: 140 }} align="left">
                                                        <DotLegalHeader marginTop={0} marginBottom={0} headerStyle="extraSmall">
                                                            {translateString("deadline")}
                                                        </DotLegalHeader>
                                                    </TableCell>
                                                )}

                                                <TableCell sx={styles.tableCellLeft} align="left">
                                                    <TableSortLabel {...getSortProps("recurringIntervalInMonths")}>
                                                        <DotLegalHeader marginTop={0} marginBottom={0} headerStyle="extraSmall">
                                                            {translateString("recurrence")}
                                                        </DotLegalHeader>
                                                    </TableSortLabel>
                                                </TableCell>

                                                {props.showGroupCompaniesColumn && (
                                                    <TableCell sx={styles.tableCellLeft} align="left">
                                                        <DotLegalHeader marginTop={0} marginBottom={0} headerStyle="extraSmall">
                                                            {translateString("groupCompanies")}
                                                        </DotLegalHeader>
                                                    </TableCell>
                                                )}

                                                <TableCell sx={styles.tableCellRight} align="right" />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedAnnualWheelActivities.map((row) => {
                                                const selection = findSelection(row.id);
                                                const editableRecurrenceText = selection
                                                    ? formatRecurringInterval(selection.interval ?? undefined, selection.intervalType ?? undefined)
                                                    : row.recurringIntervalString;

                                                return (
                                                    <TableRow key={row.id}>
                                                        <TableCell sx={{ paddingLeft: 0 }} align="left">
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                {row.name}
                                                                <DotLegalInformationTooltip
                                                                    text={row.description ? <MentionInputRender value={row.description} /> : null}
                                                                />
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ paddingLeft: 0 }} align="left">
                                                            {row.customId}
                                                        </TableCell>
                                                        {showPerRowSelectionControls && (
                                                            <TableCell sx={{ paddingLeft: 0, paddingRight: 3 }} align="left">
                                                                {selection && (
                                                                    <DotLegalDatePicker
                                                                        variant={"standard"}
                                                                        label={""}
                                                                        value={selection.deadline}
                                                                        onChange={(date) => date && props.onTemplateDeadlineChange!(row.id, date)}
                                                                        language={getDateTimePickerLanguage()}
                                                                        noMargin
                                                                        minDate={getMinDate()}
                                                                        maxDate={getMaxDate()}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                        )}
                                                        <TableCell sx={{ paddingLeft: 0 }} align="left">
                                                            {showPerRowSelectionControls ? (
                                                                <Box
                                                                    component="span"
                                                                    onClick={(e) => setRecurrenceEditor({ rowId: row.id, anchor: e.currentTarget })}
                                                                    sx={{
                                                                        cursor: "pointer",
                                                                        display: "inline-block",
                                                                        paddingTop: "4px",
                                                                        paddingBottom: "6px",
                                                                        borderBottom: "1px solid transparent",
                                                                        "&:hover": {
                                                                            borderBottomColor: "#b4b4b4",
                                                                        },
                                                                    }}
                                                                >
                                                                    {editableRecurrenceText}
                                                                </Box>
                                                            ) : (
                                                                row.recurringIntervalString
                                                            )}
                                                        </TableCell>
                                                        {props.showGroupCompaniesColumn && (
                                                            <TableCell sx={{ paddingLeft: 0, paddingRight: 1, minWidth: 200 }} align="left">
                                                                {showPerRowSelectionControls && selection && props.onTemplateGroupEntitiesChange ? (
                                                                    <DotLegalMultiSelect
                                                                        variant={"standard"}
                                                                        options={props.groupEntitiesOptions ?? []}
                                                                        onChange={(values) => props.onTemplateGroupEntitiesChange!(row.id, values)}
                                                                        placeholder={translateString("groupEntities")}
                                                                        noOptionsLabel={translateString("noOptions")}
                                                                        chosenOptions={
                                                                            selection.groupEntityIds.length === 0 ? ["null"] : selection.groupEntityIds
                                                                        }
                                                                        label={""}
                                                                        noMargin
                                                                    />
                                                                ) : row.groupCompanies.length === 0 ? (
                                                                    translateString("entireGroup")
                                                                ) : (
                                                                    row.groupCompanies.join(", ")
                                                                )}
                                                            </TableCell>
                                                        )}
                                                        <TableCell sx={{ paddingLeft: 0, paddingRight: 0 }} align="right">
                                                            <IconButton id={row.id} size="small" onClick={() => props.onRemoveAnnualWheelActivity(row.id)}>
                                                                {removeIcon}
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </DotLegalSection>
                        </Box>
                    )}
                    {showPerRowSelectionControls && (
                        <Popover
                            open={!!recurrenceEditor}
                            anchorEl={recurrenceEditor?.anchor ?? null}
                            onClose={() => setRecurrenceEditor(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "left" }}
                        >
                            <Box sx={{ p: 2, minWidth: 320 }}>
                                {editingSelection && (
                                    <RecurrenceSection
                                        interval={editingSelection.interval}
                                        intervalType={editingSelection.intervalType?.toString()}
                                        intervalTypeOptions={recurringIntervalTypeOptions}
                                        onIntervalChange={onRecurrenceIntervalChange}
                                        onIntervalTypeChange={onRecurrenceTypeChange}
                                        onSingularChange={onRecurrenceSingularChange}
                                        showSingularCheckbox
                                    />
                                )}
                            </Box>
                        </Popover>
                    )}
                </>
            )}
        </React.Fragment>
    );
}

export default SelectableAnnualWheelActivityTable;
